/**
 * Candidate Statura Care semantic design-token authority.
 *
 * Existing renderers consume candidate contracts through their rollout gates.
 * The experience programme promotes generated targets only after the rendered
 * brand packet and BRAND-CUTOVER-APPROVED gate pass.
 */

export const DESIGN_TOKEN_SCHEMA_VERSION = '1.0.0-candidate.1' as const;

export const COLOUR_MODES = ['light', 'dark', 'highContrast'] as const;
export type ColourMode = (typeof COLOUR_MODES)[number];

export const MOTION_MODES = ['fullMotion', 'reducedMotion'] as const;
export type MotionMode = (typeof MOTION_MODES)[number];

export const TYPOGRAPHY_FAMILIES = {
  display: {
    name: 'Merriweather',
    fallbackStack: ['Georgia', 'Times New Roman', 'serif'],
  },
  interface: {
    name: 'Space Grotesk',
    fallbackStack: ['Inter', 'system-ui', 'sans-serif'],
  },
} as const;

export const SUPPORTED_FONT_WEIGHTS = [400, 500, 600, 700, 900] as const;
export type SupportedFontWeight = (typeof SUPPORTED_FONT_WEIGHTS)[number];

/** Guardrail: no candidate role or adapter may request a synthetic face. */
export const FORBIDDEN_FONT_WEIGHTS = [800] as const;

export const TYPOGRAPHY_ROLES = {
  display: {
    family: 'display',
    fontSize: '2.75rem',
    lineHeight: 1.08,
    letterSpacing: '-0.01em',
    fontWeight: 900,
    constrainedChannelWeight: 700,
  },
  pageTitle: {
    family: 'display',
    fontSize: '1.625rem',
    lineHeight: 32 / 26,
    letterSpacing: '0',
    fontWeight: 700,
    constrainedChannelWeight: 700,
  },
  sectionTitle: {
    family: 'interface',
    fontSize: '1.125rem',
    lineHeight: 24 / 18,
    letterSpacing: '0',
    fontWeight: 600,
    constrainedChannelWeight: 600,
  },
  cardTitle: {
    family: 'interface',
    fontSize: '1rem',
    lineHeight: 22 / 16,
    letterSpacing: '0',
    fontWeight: 600,
    constrainedChannelWeight: 600,
  },
  bodyLarge: {
    family: 'interface',
    fontSize: '1.125rem',
    lineHeight: 1.6,
    letterSpacing: '0',
    fontWeight: 400,
    constrainedChannelWeight: 400,
  },
  body: {
    family: 'interface',
    fontSize: '0.875rem',
    lineHeight: 20 / 14,
    letterSpacing: '0',
    fontWeight: 400,
    constrainedChannelWeight: 400,
  },
  bodyComfort: {
    family: 'interface',
    fontSize: '1rem',
    lineHeight: 24 / 16,
    letterSpacing: '0',
    fontWeight: 400,
    constrainedChannelWeight: 400,
  },
  bodySmall: {
    family: 'interface',
    fontSize: '0.875rem',
    lineHeight: 20 / 14,
    letterSpacing: '0',
    fontWeight: 400,
    constrainedChannelWeight: 400,
  },
  caption: {
    family: 'interface',
    fontSize: '0.75rem',
    lineHeight: 16 / 12,
    letterSpacing: '0',
    fontWeight: 400,
    constrainedChannelWeight: 400,
  },
  eyebrow: {
    family: 'interface',
    fontSize: '0.6875rem',
    lineHeight: 16 / 11,
    letterSpacing: '0.08em',
    fontWeight: 600,
    constrainedChannelWeight: 600,
  },
  metric: {
    family: 'interface',
    fontSize: '1.75rem',
    lineHeight: 32 / 28,
    letterSpacing: '0',
    fontWeight: 700,
    constrainedChannelWeight: 700,
  },
} as const satisfies Record<
  string,
  {
    family: keyof typeof TYPOGRAPHY_FAMILIES;
    fontSize: string;
    lineHeight: number;
    letterSpacing: string;
    fontWeight: SupportedFontWeight;
    constrainedChannelWeight: SupportedFontWeight;
  }
>;

export type TypographyRole = keyof typeof TYPOGRAPHY_ROLES;

export const SEMANTIC_COLOUR_TOKEN_NAMES = [
  'identity.statura.decorative',
  'identity.statura.foreground',
  'identity.statura.hover',
  'identity.tenant.fallback',
  'surface.canvas',
  'surface.base',
  'surface.subtle',
  'surface.inverse',
  'text.primary',
  'text.secondary',
  'text.body',
  'text.muted',
  'text.inverse',
  'border.default',
  'border.strong',
  'focus.ring',
  'status.danger',
  'status.warning',
  'status.success',
  'status.information',
  'workflow.approval',
  'domain.finance',
  'program.sah',
  'program.rac',
] as const;

export type SemanticColourToken = (typeof SEMANTIC_COLOUR_TOKEN_NAMES)[number];
export type HexColour = `#${string}`;

export const RESERVED_SEMANTIC_HUE_NAMES = [
  'status.danger',
  'status.warning',
  'status.success',
  'status.information',
  'workflow.approval',
  'domain.finance',
  'program.sah',
  'program.rac',
] as const satisfies readonly SemanticColourToken[];

export type ReservedSemanticHue = (typeof RESERVED_SEMANTIC_HUE_NAMES)[number];

type SemanticMode = Record<SemanticColourToken, HexColour>;

export const STATUS_PAIRS = {
  success: { fg: '#166534', bg: '#F0FDF4', fill: '#22C55E' },
  warning: { fg: '#92400E', bg: '#FFFBEB', fill: '#F59E0B' },
  danger: { fg: '#991B1B', bg: '#FEF2F2', fill: '#EF4444' },
  information: { fg: '#3730A3', bg: '#EEF2FF', fill: '#6366F1' },
  critical: { fg: '#7F1D1D', bg: '#FEE2E2', fill: '#B91C1C' },
  neutral: { fg: '#475569', bg: '#F1F5F9', fill: '#64748B' },
} as const;

export const SEMANTIC_COLOUR_TOKENS = {
  light: {
    'identity.statura.decorative': '#96A998',
    'identity.statura.foreground': '#3E5D4A',
    'identity.statura.hover': '#2F4B3B',
    'identity.tenant.fallback': '#3E5D4A',
    'surface.canvas': '#F8FAFC',
    'surface.base': '#FFFFFF',
    'surface.subtle': '#F5F6F2',
    'surface.inverse': '#0F172A',
    'text.primary': '#1E293B',
    'text.secondary': '#475569',
    'text.body': '#334155',
    'text.muted': '#475569',
    'text.inverse': '#FFFFFF',
    'border.default': '#CBD5E1',
    'border.strong': '#64748B',
    'focus.ring': '#3E5D4A',
    'status.danger': STATUS_PAIRS.danger.fg,
    'status.warning': STATUS_PAIRS.warning.fg,
    'status.success': STATUS_PAIRS.success.fg,
    'status.information': STATUS_PAIRS.information.fg,
    'workflow.approval': '#6D28D9',
    'domain.finance': '#0369A1',
    'program.sah': '#0F766E',
    'program.rac': '#7E22CE',
  },
  dark: {
    'identity.statura.decorative': '#AFC1B1',
    'identity.statura.foreground': '#C5D6C7',
    'identity.statura.hover': '#C5D6C7',
    'identity.tenant.fallback': '#C5D6C7',
    'surface.canvas': '#0F172A',
    'surface.base': '#1E293B',
    'surface.subtle': '#273449',
    'surface.inverse': '#FFFFFF',
    'text.primary': '#F8FAFC',
    'text.secondary': '#CBD5E1',
    'text.body': '#E2E8F0',
    'text.muted': '#94A3B8',
    'text.inverse': '#0F172A',
    'border.default': '#64748B',
    'border.strong': '#CBD5E1',
    'focus.ring': '#C5D6C7',
    'status.danger': '#FCA5A5',
    'status.warning': '#FCD34D',
    'status.success': '#86EFAC',
    'status.information': '#93C5FD',
    'workflow.approval': '#C4B5FD',
    'domain.finance': '#7DD3FC',
    'program.sah': '#5EEAD4',
    'program.rac': '#D8B4FE',
  },
  highContrast: {
    'identity.statura.decorative': '#000000',
    'identity.statura.foreground': '#000000',
    'identity.statura.hover': '#000000',
    'identity.tenant.fallback': '#000000',
    'surface.canvas': '#FFFFFF',
    'surface.base': '#FFFFFF',
    'surface.subtle': '#FFFFFF',
    'surface.inverse': '#000000',
    'text.primary': '#000000',
    'text.secondary': '#111111',
    'text.body': '#000000',
    'text.muted': '#1A1A1A',
    'text.inverse': '#FFFFFF',
    'border.default': '#000000',
    'border.strong': '#000000',
    'focus.ring': '#005FCC',
    'status.danger': '#B00020',
    'status.warning': '#7A3E00',
    'status.success': '#006B2E',
    'status.information': '#0039A6',
    'workflow.approval': '#4A148C',
    'domain.finance': '#004B73',
    'program.sah': '#005A52',
    'program.rac': '#5E168F',
  },
} as const satisfies Record<ColourMode, SemanticMode>;

export const MOTION_TOKENS = {
  fullMotion: {
    durations: { immediate: '0ms', fast: '150ms', normal: '200ms', deliberate: '500ms' },
    easing: { productive: 'ease-out' },
    allowDecorativeMotion: true,
  },
  reducedMotion: {
    durations: { immediate: '0ms', fast: '0ms', normal: '0ms', deliberate: '0ms' },
    easing: { productive: 'linear' },
    allowDecorativeMotion: false,
  },
} as const satisfies Record<
  MotionMode,
  {
    durations: Record<'immediate' | 'fast' | 'normal' | 'deliberate', string>;
    easing: { productive: string };
    allowDecorativeMotion: boolean;
  }
>;

export const TENANT_IDENTITY_COLOUR_POLICY = {
  version: '1.0.0-candidate.1',
  normalisation: 'UPPERCASE_SIX_DIGIT_HEX_ONLY',
  contrastAlgorithm: 'WCAG_2_RELATIVE_LUMINANCE',
  minimumContrastRatioAgainstSurfaceBase: 3,
  textContrastRatios: { normal: 4.5, large: 3 },
  deltaAlgorithm: 'CIEDE2000',
  minimumDeltaE00FromEveryReservedHue: 15,
  collisionDisposition: 'CANONICAL_MODE_FALLBACK_NO_TRANSFORM',
  invalidDisposition: 'CANONICAL_MODE_FALLBACK',
  missingDisposition: 'CANONICAL_MODE_FALLBACK',
  prohibitedUses: ['status', 'urgency', 'program-identity', 'safety-chrome'],
} as const;

export const PROGRAM_CONTEXT_POLICY = {
  version: '1.0.0-candidate.1',
  supportedPrograms: ['SAH', 'RAC', 'MIXED'],
  requiredCarriers: ['explicit_text', 'information_structure'],
  optionalCarriers: ['icon', 'secondary_colour'],
  colourOnlyProgramIdentityForbidden: true,
  statusColourReuseForbidden: true,
} as const;

/** Shared presentation roles; identity rollout policy remains separate. */
export const FOUNDATION_COLOURS = {
  'action.primary': '#3E5D4A',
  'action.primary-hover': '#2F4B3B',
  'action.primary-foreground': '#FFFFFF',
  'surface.productive': '#F7F8F4',
  'surface.sheet': '#FFFFFF',
  'surface.soft': '#F5F6F2',
  'selection.bg': '#E8EEE9',
  'selection.fg': '#2F4B3B',
  'border.control': '#7B8C7D',
  'focus.control': '#3E5D4A',
} as const;

/** Visual spacing only. Does not select rollout, disclosure or roster density. */
export const PRODUCTIVE_DENSITY = {
  'page-gutter': '16px',
  'page-gutter-wide': '24px',
  'panel-inset': '20px',
  'panel-inset-compact': '16px',
  'radius-surface': '12px',
  'radius-control': '8px',
  'radius-badge': '4px',
  'radius-tile': '12px',
  'radius-overlay': '12px',
  'control-min-height': '44px',
  'toolbar-min-height': '36px',
  'compact-control-min-height': '32px',
  'table-row-min-height': '44px',
  'section-gap': '32px',
  'elevation-surface': 'none',
  'elevation-menu': '0 4px 6px rgba(15, 23, 42, 0.07)',
  'elevation-popover': '0 6px 12px rgba(15, 23, 42, 0.10)',
  'elevation-dialog': '0 20px 40px rgba(15, 23, 42, 0.12)',
} as const;

export const COMFORT_DENSITY = {
  'radius-surface': '20px',
  'radius-control': '12px',
  'panel-inset': '28px',
  'elevation-surface': '0 12px 30px rgba(15, 23, 42, 0.06)',
} as const;

export const DESIGN_TOKEN_SOURCE = {
  schemaVersion: DESIGN_TOKEN_SCHEMA_VERSION,
  status: 'candidate_only',
  foundation: {
    colours: FOUNDATION_COLOURS,
    statusPairs: STATUS_PAIRS,
    productiveDensity: PRODUCTIVE_DENSITY,
    comfortDensity: COMFORT_DENSITY,
  },
  colourModes: COLOUR_MODES,
  motionModes: MOTION_MODES,
  colours: SEMANTIC_COLOUR_TOKENS,
  typography: {
    families: TYPOGRAPHY_FAMILIES,
    supportedWeights: SUPPORTED_FONT_WEIGHTS,
    forbiddenWeights: FORBIDDEN_FONT_WEIGHTS,
    roles: TYPOGRAPHY_ROLES,
  },
  motion: MOTION_TOKENS,
  tenantIdentityColourPolicy: TENANT_IDENTITY_COLOUR_POLICY,
  programContextPolicy: PROGRAM_CONTEXT_POLICY,
} as const;
